import {
  type DiscordConnection,
  type DiscordOAuthCallbackResponse,
  DISCORD_CONNECTION_CLIENT_COLUMNS_CSV,
  DiscordConnectionRowResponseSchema,
  DiscordOAuthCallbackResponseSchema,
  DiscordOAuthStartResponseSchema,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// Discord connection lookups + OAuth round-trip helpers used by the Discord
// settings card and the post-game-creation onboarding step.
//
// All edge-function calls go through supabase.functions.invoke so the user's
// JWT travels in the Authorization header and the function can validate
// studio membership server-side.
// ---------------------------------------------------------------------------

const CLIENT_FACING_COLUMNS = DISCORD_CONNECTION_CLIENT_COLUMNS_CSV;

// ---------------------------------------------------------------------------
// Read: connection attached to a single game.
// ---------------------------------------------------------------------------

export async function fetchDiscordConnectionForGame(
  gameId: string,
): Promise<DiscordConnection | null> {
  // Two-step: read the game's discord_connection_id, then the row. Avoids a
  // FK join shape that supabase-js sometimes mangles when columns are
  // column-level revoked.
  const { data: gameRow, error: gameErr } = await supabase
    .from('games')
    .select('id, discord_connection_id')
    .eq('id', gameId)
    .single();
  if (gameErr !== null) {
    console.warn('[discord-data] could not load game row:', gameErr.message);
    return null;
  }
  if (gameRow?.discord_connection_id === null || gameRow?.discord_connection_id === undefined) {
    return null;
  }

  const { data: connectionRow, error: connErr } = await supabase
    .from('discord_connections')
    .select(CLIENT_FACING_COLUMNS)
    .eq('id', gameRow.discord_connection_id as string)
    .single();
  if (connErr !== null || connectionRow === null) {
    console.warn('[discord-data] could not load connection row:', connErr?.message);
    return null;
  }

  const parsed = DiscordConnectionRowResponseSchema.safeParse(connectionRow);
  if (!parsed.success) {
    console.warn(
      '[discord-data] could not parse connection row:',
      parsed.error.issues,
    );
    return null;
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Edge function: start. Returns the Discord consent URL for the portal to
// redirect to.
// ---------------------------------------------------------------------------

export type StartDiscordConnectInput = {
  studioId: string;
  gameId: string;
};

export type StartDiscordConnectError =
  | 'not_authenticated'
  | 'not_a_member'
  | 'game_not_found'
  | 'game_studio_mismatch'
  | 'server_misconfigured'
  | 'persist_failed'
  | 'unknown';

export type StartDiscordConnectResult =
  | { ok: true; url: string }
  | { ok: false; code: StartDiscordConnectError };

export async function startDiscordConnect(
  input: StartDiscordConnectInput,
): Promise<StartDiscordConnectResult> {
  const { data, error } = await supabase.functions.invoke<{
    url?: string;
    state?: string;
    error?: string;
  }>('discord-oauth', {
    body: {
      action: 'start',
      studioId: input.studioId,
      gameId: input.gameId,
    },
  });

  // supabase-js returns errors via FunctionsHttpError with a JSON body; we
  // try the JSON `error` field first, then fall back to the network error.
  const code =
    data !== null && data !== undefined && typeof data === 'object' && 'error' in data
      ? (data as { error?: string }).error
      : undefined;

  if (error !== null || code !== undefined) {
    return { ok: false, code: (code ?? 'unknown') as StartDiscordConnectError };
  }

  const parsed = DiscordOAuthStartResponseSchema.safeParse(data);
  if (!parsed.success) {
    return { ok: false, code: 'unknown' };
  }
  return { ok: true, url: parsed.data.url };
}

// ---------------------------------------------------------------------------
// Edge function: callback. Exchanges code+state with Discord and persists
// the connection.
// ---------------------------------------------------------------------------

export type CompleteDiscordConnectInput = {
  code: string;
  state: string;
  guildId: string;
};

export type CompleteDiscordConnectError =
  | 'not_authenticated'
  | 'invalid_state'
  | 'state_user_mismatch'
  | 'state_expired'
  | 'state_missing_game'
  | 'guild_mismatch'
  | 'discord_token_exchange_failed'
  | 'discord_guild_lookup_failed'
  | 'game_not_found'
  | 'server_misconfigured'
  | 'persist_failed'
  | 'unknown';

export type CompleteDiscordConnectResult =
  | { ok: true; response: DiscordOAuthCallbackResponse }
  | { ok: false; code: CompleteDiscordConnectError };

export async function completeDiscordConnect(
  input: CompleteDiscordConnectInput,
): Promise<CompleteDiscordConnectResult> {
  const { data, error } = await supabase.functions.invoke<{
    outcome?: string;
    error?: string;
  }>('discord-oauth', {
    body: {
      action: 'callback',
      code: input.code,
      state: input.state,
      guildId: input.guildId,
    },
  });

  const code =
    data !== null && data !== undefined && typeof data === 'object' && 'error' in data
      ? (data as { error?: string }).error
      : undefined;

  if (error !== null || code !== undefined) {
    return { ok: false, code: (code ?? 'unknown') as CompleteDiscordConnectError };
  }

  const parsed = DiscordOAuthCallbackResponseSchema.safeParse(data);
  if (!parsed.success) {
    console.warn('[discord-data] callback response did not parse', parsed.error.issues);
    return { ok: false, code: 'unknown' };
  }
  return { ok: true, response: parsed.data };
}

// ---------------------------------------------------------------------------
// Disconnect: soft-deletes the connection. The connection row stays for
// audit (status='disconnected'); games keep their FK so the portal can show
// "previously connected to {server name}" if useful later. The bot is NOT
// removed from the guild automatically - the disconnect dialog tells the
// user they may want to also kick the bot from Discord.
// ---------------------------------------------------------------------------

export type DisconnectError = 'not_a_member' | 'connection_not_found' | 'unknown';

export type DisconnectResult = { ok: true } | { ok: false; code: DisconnectError };

export async function disconnectDiscord(connectionId: string): Promise<DisconnectResult> {
  // Soft-delete via security-definer RPC (migration 0012). Status flips to
  // 'disconnected', tokens are nulled, attached games are detached, and a
  // 'discord_connection_leave' NOTIFY tells the bot to leave the guild on
  // Discord's side. The disconnected row is preserved for audit; the
  // partial-unique index on guild_id excludes disconnected rows so the
  // same studio can re-install the same server later.
  const { error } = await supabase.rpc('disconnect_discord_connection', {
    p_connection_id: connectionId,
  });
  if (error !== null) {
    console.warn('[discord-data] disconnect failed:', error.message);
    if (error.message.includes('not_a_member')) {
      return { ok: false, code: 'not_a_member' };
    }
    if (error.message.includes('connection_not_found')) {
      return { ok: false, code: 'connection_not_found' };
    }
    return { ok: false, code: 'unknown' };
  }
  return { ok: true };
}

// ---------------------------------------------------------------------------
// On-demand health re-check. The bot subscribes to the
// 'discord_connection_recheck' NOTIFY (PR3) and runs a verify pass when it
// fires. Until then the RPC is a safe no-op for the bot.
// ---------------------------------------------------------------------------

export async function requestDiscordRecheck(connectionId: string): Promise<void> {
  const { error } = await supabase.rpc('request_discord_connection_recheck', {
    p_connection_id: connectionId,
  });
  if (error !== null) {
    console.warn('[discord-data] recheck request failed:', error.message);
  }
}
